import { useEnum } from "@/api/services/global/enum";
import { updateOrderTaxRateApi, useGetTaxRateList } from "@/api/services/global/master";
import { TGetTaxRateListOutputDto } from "@/api/services/global/master/dtos/taxRate/get-master-tax-rate-list.dto";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT, DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { HiddenTaxRateModal } from "@/features/master-data/components/TaxRate/HiddenTaxRateModal";
import { TaxRateFormModal } from "@/features/master-data/components/TaxRate/TaxRateFormModal";
import { StyledMasterDataContainer, StyledMasterDataDragTable } from "@/features/master-data/components/masterDataTableStyles";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { formatRateToPercentText } from "@/features/master-data/utils/taxRate";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { blue, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { toast } from "@/utils/toast";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";

export const MasterDataTaxRate = () => {
  const formModal = useVisible();
  const [hiddenTaxRate, setHiddenTaxRate] = useState<TGetTaxRateListOutputDto>();
  const [selectedTaxRate, setSelectedTaxRate] = useState<TGetTaxRateListOutputDto>();
  const [enableSaveOrderBtn, setEnableOrderBtn] = useState<boolean>(false);
  const { data, isLoading } = useGetTaxRateList();
  // 表示ラベルは BE の enum を参照して画面間でぶれないようにする
  const { data: taxCategoryOptions } = useEnum("ETaxCategory");

  const [rows, setRows] = useState<TGetTaxRateListOutputDto[]>(data);

  useEffect(() => {
    if (data && data.length > 0) setRows(data);
  }, [data]);

  /** 並び替えのみを保存する。配列の並び順がそのまま新しい表示順序になる */
  const handleSaveOrder = async () => {
    try {
      const rs = await updateOrderTaxRateApi({ taxRateIds: rows.map((item) => toNumber(item.id)) });
      setRows(rs.data);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // Handle row dragging
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
    setEnableOrderBtn(true);
  };

  const getTextColor = (row: TGetTaxRateListOutputDto) => (row.isHidden ? text.tertiary : text.primary);

  if (isLoading) return <LoadingPage />;

  return (
    <Stack sx={{ mt: 1 }}>
      <TopPage title={"「" + MASTER_DATA_TYPE_TITLE.TAX_RATE + "」の編集"} backUrl={AppRoutes.masterData} enablePageBack={false} />
      <StyledMasterDataContainer>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
            {/* 過去の請求の税率が変わらないよう、改定は行追加で運用する */}
            <Typography variant="cap12" color={text.secondary}>
              税率改定時は既存行を書き換えず、既存行に「適用終了日」を設定し、改定日を「適用開始日」とする新しい行を追加してください。過去の請求が参照しているため行の削除はできません（無効化のみ）。
            </Typography>
            <Button size="sm" onClick={() => formModal.open()}>
              項目追加
            </Button>
          </Stack>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="tableRows" direction="vertical">
              {(provided) => (
                <StyledMasterDataDragTable {...provided.droppableProps} ref={provided.innerRef}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>税率系列</TableCell>
                        <TableCell>表示名</TableCell>
                        <TableCell>課税区分</TableCell>
                        <TableCell>税率</TableCell>
                        <TableCell>適用開始日</TableCell>
                        <TableCell>適用終了日</TableCell>
                        <TableCell>最終更新日</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>有効／無効</TableCell>
                        <TableCell>編集</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => (
                        <Draggable key={row.id} draggableId={row.id.toString()} index={index}>
                          {(provided) => (
                            <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                              <TableCell sx={{ width: "3%" }} {...provided.dragHandleProps}>
                                <IconOrderTable color={blue[80]} />
                              </TableCell>
                              <TableCell sx={{ width: "14%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {row.taxRateSeries || EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "14%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {row.displayName || EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "9%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {findLabelByValue(taxCategoryOptions, row.taxCategory) || EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "7%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {formatRateToPercentText(row.rate)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "11%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {row.effectiveFrom ? dayjs(row.effectiveFrom).format(DATE_FORMAT) : EMPTY_STR.DATE}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "11%" }}>
                                <Typography color={getTextColor(row)} variant="body14">
                                  {row.effectiveTo ? dayjs(row.effectiveTo).format(DATE_FORMAT) : EMPTY_STR.TEXT}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "15%" }}>
                                <Typography color={getTextColor(row)} sx={{ px: 0.5 }} variant="body14">
                                  {row.updatedAt ? dayjs(row.updatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ width: "8%" }} align="center">
                                <Switch checked={!row.isHidden} onChange={() => setHiddenTaxRate(row)} color="primary" />
                              </TableCell>
                              <TableCell sx={{ width: "8%" }}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedTaxRate(row);
                                    formModal.open();
                                  }}
                                >
                                  編集
                                </Button>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  </Table>
                </StyledMasterDataDragTable>
              )}
            </Droppable>
          </DragDropContext>
        </Stack>

        {formModal.visible && (
          <TaxRateFormModal
            onClose={() => {
              formModal.close();
              setSelectedTaxRate(undefined);
            }}
            isOpen={formModal.visible}
            selectedTaxRate={selectedTaxRate}
            taxRates={rows}
          />
        )}

        <HiddenTaxRateModal hiddenTaxRate={hiddenTaxRate} setHiddenTaxRate={setHiddenTaxRate} />
        <ConfirmLeavePage shouldBlock={enableSaveOrderBtn}></ConfirmLeavePage>
      </StyledMasterDataContainer>
      <FooterActionBar title="オーダーを保存する" onSubmit={handleSaveOrder} backUrl={AppRoutes.masterData} isDisabled={!enableSaveOrderBtn} />
    </Stack>
  );
};
